import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../api/client';
import type { Organization, Title } from '@dfx-transfer/shared-types';
import { RoleGuard } from '../components/RoleGuard';
import { useTransferUser } from '../auth/useTransferUser';

export function TitlesPage() {
  return (
    <RoleGuard allow={['dfx_super_admin', 'org_admin', 'org_uploader']}>
      <TitlesPageInner />
    </RoleGuard>
  );
}

function TitlesPageInner() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { user } = useTransferUser();
  const [orgId, setOrgId] = useState<string | null>(null);
  const [externalId, setExternalId] = useState('');
  const [titleText, setTitleText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const orgs = useQuery({
    enabled: user?.role === 'dfx_super_admin',
    queryKey: ['orgs'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return apiRequest<Organization[]>('/api/v1/orgs', { token });
    },
  });

  useEffect(() => {
    if (orgId) return;
    if (user && user.role !== 'dfx_super_admin' && user.org_id) setOrgId(user.org_id);
    else if (orgs.data && orgs.data[0]) setOrgId(orgs.data[0].id);
  }, [orgs.data, user, orgId]);

  const titles = useQuery({
    enabled: !!orgId,
    queryKey: ['titles', orgId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return apiRequest<Title[]>(`/api/v1/orgs/${orgId}/titles`, { token });
    },
  });

  const create = useMutation({
    mutationFn: async (input: { external_id: string; title: string }) => {
      const token = await getAccessTokenSilently();
      return apiRequest<Title>(`/api/v1/orgs/${orgId}/titles`, {
        token,
        method: 'POST',
        body: { ...input, content_type: 'movie' },
      });
    },
    onSuccess: () => {
      setExternalId('');
      setTitleText('');
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['titles', orgId] });
    },
    onError: (err: Error) => setError(err.message),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!orgId || !externalId || !titleText) return;
    create.mutate({ external_id: externalId, title: titleText });
  }

  const canCreate = user?.role === 'dfx_super_admin' || user?.role === 'org_admin';

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold">Titles</h1>
        <p className="mt-1 text-[color:var(--color-text-secondary)]">
          A title is a movie or series. Once created, upload the metadata CSV and assets
          into the title's folder over SFTP.
        </p>
      </header>

      {user?.role === 'dfx_super_admin' && (
        <label className="block max-w-sm">
          <span className="text-sm text-[color:var(--color-text-secondary)]">Organization</span>
          <select
            value={orgId ?? ''}
            onChange={(e) => setOrgId(e.target.value || null)}
            className="mt-1 w-full rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-primary)] px-3 py-2"
          >
            {(orgs.data ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {canCreate && (
        <section className="rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-6">
          <h2 className="mb-4 text-lg font-medium">Create movie title</h2>
          <p className="mb-4 text-sm text-[color:var(--color-text-secondary)]">
            Series support lands in v1.1. The <code>External ID</code> becomes the SFTP folder
            name — keep it short and filesystem-safe.
          </p>
          <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm text-[color:var(--color-text-secondary)]">External ID</span>
              <input
                value={externalId}
                onChange={(e) =>
                  setExternalId(e.target.value.replace(/[^A-Za-z0-9_.-]/g, '-'))
                }
                placeholder="the-demolitionist"
                className="mt-1 w-full rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-primary)] px-3 py-2 font-mono text-sm"
                required
              />
            </label>
            <label className="block">
              <span className="text-sm text-[color:var(--color-text-secondary)]">Display title</span>
              <input
                value={titleText}
                onChange={(e) => setTitleText(e.target.value)}
                placeholder="The Demolitionist"
                className="mt-1 w-full rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-primary)] px-3 py-2"
                required
              />
            </label>
            {error && (
              <div className="sm:col-span-2 rounded border border-[color:var(--color-error-muted)] p-3 text-sm text-[color:var(--color-error)]">
                {error}
              </div>
            )}
            <div className="sm:col-span-2 flex items-center gap-4">
              <button
                type="submit"
                disabled={!orgId || create.isPending}
                className="rounded bg-[color:var(--color-crimson)] px-5 py-2 font-medium text-white disabled:opacity-50"
              >
                {create.isPending ? 'Creating…' : 'Create title'}
              </button>
              <a
                href={`${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1/templates/movie-metadata.csv`}
                className="text-sm text-[color:var(--color-text-secondary)] underline"
              >
                Download metadata CSV template
              </a>
            </div>
          </form>
        </section>
      )}

      <section>
        <h2 className="mb-4 text-lg font-medium">All titles</h2>
        {titles.isLoading ? (
          <div>Loading…</div>
        ) : titles.data?.length ? (
          <ul className="divide-y divide-[color:var(--color-border)] rounded border border-[color:var(--color-border)]">
            {titles.data.map((t) => (
              <li key={t.id} className="px-4 py-3">
                <Link
                  to={`/titles/${t.id}`}
                  className="flex items-center justify-between gap-4"
                >
                  <div>
                    <div className="font-medium">{t.title}</div>
                    <div className="font-mono text-xs text-[color:var(--color-text-muted)]">
                      {t.external_id} · {t.content_type}
                    </div>
                  </div>
                  <StatePill state={t.state} />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-[color:var(--color-text-secondary)]">No titles yet.</div>
        )}
      </section>
    </div>
  );
}

function StatePill({ state }: { state: Title['state'] }) {
  const map: Record<Title['state'], { label: string; color: string }> = {
    draft: { label: 'Draft', color: 'var(--color-text-muted)' },
    awaiting_upload: { label: 'Awaiting upload', color: 'var(--color-warning)' },
    uploading: { label: 'Uploading', color: 'var(--color-info)' },
    validating: { label: 'Validating', color: 'var(--color-info)' },
    ready: { label: 'Ready', color: 'var(--color-success)' },
    failed: { label: 'Failed', color: 'var(--color-error)' },
  };
  const m = map[state];
  return (
    <span
      className="rounded-full border px-2 py-0.5 font-mono text-xs"
      style={{ color: m.color, borderColor: m.color }}
    >
      {m.label}
    </span>
  );
}
